import { countryIcons, TABLE_COLUMN_TYPE } from "../../../constants"
import { SpotifyChartEntry, TableColumn } from "../../../types"
import { KSpan, KTitleSpan, KTooltip } from "kahuna-base-react-components"
import { addCommasToNumber, formatIsoDate } from "../../../utility"
import ImageWithFallback from "../../ui/components/ImageWithFallback"
import { lang } from "../../../constants/languages"
//@ts-ignore
import ArtistIcon from "../../../assets/spotify-charts/microphone.svg"
//@ts-ignore
import TrackIcon from "../../../assets/spotify-charts/music-note-1.svg"
//@ts-ignore
import AlbumIcon from "../../../assets/spotify-charts/record-label.svg"
//@ts-ignore
import ArtistBlackIcon from "../../../assets/spotify-charts/microphone-black.svg"
//@ts-ignore
import TrackBlackIcon from "../../../assets/spotify-charts/music-note-1-black.svg"
//@ts-ignore
import AlbumBlackIcon from "../../../assets/spotify-charts/record-label-black.svg"
import { countryMapEn } from "../../../constants/languages/countryMap/en"
//@ts-ignore
import EarthIcon from "../../../assets/country-icons/earth.svg"
//@ts-ignore
import ArrowUpIcon from "../../../assets/spotify-charts/arrow-up-green.svg"
//@ts-ignore
import ArrowDownIcon from "../../../assets/spotify-charts/arrow-down-red.svg"
//@ts-ignore
import InfoCircle from "../../../assets/analytics_icons/info-circle.svg"
import TruncatedListWithTooltip from "../playlists/components/TruncatedListWithTooltip"

const EntryStatusOptions = {
  MOVED_DOWN: "MOVED_DOWN",
  MOVED_UP: "MOVED_UP",
  NEW_ENTRY: "NEW_ENTRY",
  RE_ENTRY: "RE_ENTRY",
  NO_CHANGE: "NO_CHANGE"
}

const EntryStatusLabelOptions = {
  NEW_ENTRY: lang.spotify_chart_entries.new_entry,
  RE_ENTRY: lang.spotify_chart_entries.re_entry,
  NO_CHANGE: "-"
}

const chartBadge = (icon: string, text: string, color: string, borderColor: string, bgColor: string) => {
  return (
    <div
      className="px-2 py-1 gap-1.5 flex items-center"
      style={{
        border: `1px solid ${borderColor}`,
        borderRadius: 6,
        backgroundColor: bgColor
      }}
    >
      <img src={icon} className="max-w-5 max-h-5 min-w-5 min-h-5" />
      <KSpan text={text} color={color} fontWeight={500} />
    </div>
  )
}

const getCountryIcon = (countryKey: string) => {
  const countryMap = countryMapEn
  const countryNameEn = countryMap[countryKey as keyof typeof countryMap]
  const icon = countryIcons?.[countryNameEn as keyof typeof countryIcons] || EarthIcon
  return icon
}

const spotifyChartEntriesColumns = (selectedChartType: string): TableColumn[] => {
  const spotifyChartColumn: TableColumn = {
    header: lang.spotify_chart_entries.chart,
    headerStyle: {
      textAlign: "left",
      width: selectedChartType === "track" ? "30%" : "35%"
    },
    overrideHeader: () => <KSpan text={lang.spotify_chart_entries.chart} color="#111" fontWeight={500} fontSize={16} />,

    type: TABLE_COLUMN_TYPE.FUNCTION,
    render: (row: SpotifyChartEntry) => {
      let icon = ArtistIcon
      let text = lang.spotify_chart_entries.artist_badge
      if (selectedChartType === "track") {
        icon = TrackIcon
        text = lang.spotify_chart_entries.track_badge
      } else if (selectedChartType === "album") {
        icon = AlbumIcon
        text = lang.spotify_chart_entries.album_badge
      }

      const color = "#818181"
      const bgColor = "#8686861A"
      const borderColor = "#999"

      const countryIcon = getCountryIcon(row?.spotifyChart?.country ?? "")

      return (
        <div
          className="w-full flex gap-2 whitespace-nowrap text-ellipsis h-[180px] max-w-[440px] pr-5"
          style={{
            cursor: row?.spotifyChart?.alias ? "pointer" : "default"
          }}
          onClick={() => {
            if (!row?.spotifyChart?.alias) return
            window.open(`https://charts.spotify.com/charts/view/${row?.spotifyChart?.alias}/latest`, "_blank")
          }}
        >
          <div
            className="w-full flex flex-col justify-start h-full py-4 pr-4 pl-0"
            style={{
              //border: "1px solid #F2F2F3",
              borderRadius: 10
            }}
          >
            <div className="w-full flex flex-row items-center gap-3.5 ">
              <div className="w-full flex flex-col gap-1">
                <div className="flex w-full justify-between items-center">
                  {chartBadge(icon, text, color, borderColor, bgColor)}
                  <div
                    className="flex gap-1.5 items-center rounded-[5px] p-1"
                    style={{
                      border: "1px solid #F2F2F3"
                    }}
                  >
                    <img src={countryIcon} className="min-w-5 min-h-5 max-w-5 max-h-5" />
                    <KSpan text={row?.spotifyChart?.country ?? ""} fontWeight={600} color="#222" />
                  </div>
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-1 mt-3 items-start">
              <div className="flex items-center !cursor-default" onClick={(event) => event.stopPropagation()}>
                <KTitleSpan
                  text={row?.spotifyChart?.name}
                  color="#111"
                  fontSize={20}
                  lineHeight="24px"
                  fontWeight={500}
                />
              </div>
              <div className="flex items-center !cursor-default" onClick={(event) => event.stopPropagation()}>
                <KSpan text={row?.spotifyChart?.alias} fontSize={13} fontWeight={500} />
              </div>
            </div>
            <div
              className="flex flex-row justify-between pt-2 mt-4"
              style={{
                borderTop: "1px solid #F2F2F3"
              }}
            >
              <div className="flex flex-col">
                <KSpan text={lang.spotify_chart_entries.earliest} fontWeight={500} />
                <KSpan text={row?.spotifyChart?.earliestDate} fontSize={13} color="#000" />
              </div>
              <div className="flex flex-col items-end">
                <KSpan text={lang.spotify_chart_entries.latest} fontWeight={500} />
                <KSpan text={row?.spotifyChart?.latestDate} fontSize={13} color="#000" />
              </div>
            </div>
          </div>
        </div>
      )
    }
  }
  const entryColumn: TableColumn = {
    header: "",
    headerStyle: {
      textAlign: "left",
      width: selectedChartType === "artist" ? "25%" : selectedChartType === "track" ? "35%" : "40%"
    },
    overrideHeader: () => (
      <KSpan
        text={
          selectedChartType === "track"
            ? lang.spotify_chart_entries.track
            : selectedChartType === "album"
            ? lang.spotify_chart_entries.release
            : lang.spotify_chart_entries.artist
        }
        color="#111"
        fontWeight={500}
        fontSize={16}
      />
    ),
    type: TABLE_COLUMN_TYPE.FUNCTION,
    render: (row: SpotifyChartEntry) => {
      let artists = row?.track?.artistList
      let image = row?.track?.release?.coverFileUrl
      let defaultImage = TrackBlackIcon
      let title = row?.trackName
      let targetUrl = `https://open.spotify.com/track/${row?.track?.spotifyId}`

      if (selectedChartType === "album") {
        //@ts-ignore
        artists = row?.release?.artistList
        image = row?.release?.coverFileUrl
        defaultImage = AlbumBlackIcon
        title = row?.releaseName
        targetUrl = `https://open.spotify.com/album/${row?.track?.spotifyId}`
      } else if (selectedChartType === "artist") {
        artists = []
        image = row?.user?.artistImageUrl
        defaultImage = ArtistBlackIcon
        title = row?.userName
        targetUrl = `https://open.spotify.com/artist/${row?.user?.artistSpotifyId}`
      }
      //@ts-ignore
      const artistList = artists.map((artist) => artist.name).sort((a, b) => a.length - b.length)

      return (
        <div
          className="flex gap-2 items-center whitespace-nowrap max-w-full h-[180px]"
          style={{
            //border: "1px solid #F2F2F3",
            borderRadius: 10
          }}
        >
          <ImageWithFallback
            overrideDefaultImage={defaultImage}
            overrideTargetUrl={targetUrl}
            coverFileUrl={image || ""}
            width={130}
            height={130}
          />
          <div className="h-full flex flex-col justify-center gap-4 py-3">
            <div className="max-[1500px]:max-w-[250px] min-[1500px]:max-w-[380px] pt-0.5 pr-1">
              <KTitleSpan
                text={title ?? ""}
                color="#000"
                fontWeight={500}
                fontSize={16}
                lineHeight="16px"
                ellipsis={!(selectedChartType === "artist")}
              />
            </div>
            {selectedChartType === "track" && (
              <div className="rounded-[4px] bg-[#F7F7F7] flex items-center p-1 gap-1 whitespace-nowrap w-min">
                <KSpan
                  text={`${lang.spotify_chart_entries.release}:`}
                  color="#111"
                  fontSize={11}
                  fontWeight={500}
                  lineHeight="12px"
                  letterSpacing="0.22px"
                />
                <KSpan
                  text={`${row?.track?.release?.title}`}
                  color="#111"
                  fontSize={11}
                  fontWeight={500}
                  lineHeight="12px"
                  letterSpacing="0.22px"
                />
              </div>
            )}
            {!(selectedChartType === "artist") && (
              <div className="rounded-[4px] bg-[#F7F7F7] flex items-center p-1 gap-1 flex-wrap w-min">
                <KSpan
                  text={`${lang.spotify_chart_entries.release_date}: ${formatIsoDate(
                    selectedChartType === "track"
                      ? row?.track?.release?.releaseDate ?? ""
                      : row?.release?.releaseDate ?? "",
                    true
                  )}`}
                  color="#111"
                  fontSize={11}
                  fontWeight={500}
                  lineHeight="12px"
                  letterSpacing="0.22px"
                />
              </div>
            )}
            {!(selectedChartType === "artist") && (
              <TruncatedListWithTooltip
                itemList={artistList && artistList.length > 0 ? artistList : ["-"]}
                visibleItemCount={3}
                breakPointInPx={1400}
                smallScreenVisibleItemCount={2}
                title={`${lang.spotify_chart_entries.artist}:`}
              />
            )}
            {!(selectedChartType === "artist") && (
              <div className="rounded-[4px] bg-[#F7F7F7] flex items-center p-1 w-min">
                <KSpan
                  text={`${
                    selectedChartType === "track"
                      ? `${lang.spotify_chart_entries.isrc}: ${row?.track?.isrc || "-"}`
                      : `${lang.spotify_chart_entries.upc}: ${row?.release?.upc || "-"}`
                  }`}
                  color="#111"
                  fontSize={11}
                  fontWeight={500}
                  lineHeight="12px"
                  letterSpacing="0.22px"
                />
              </div>
            )}
          </div>
        </div>
      )
    }
  }
  const positionColumn: TableColumn = {
    header: lang.spotify_chart_entries.position,
    headerStyle: {
      textAlign: "left",
      width: selectedChartType === "track" ? "20%" : "30%"
    },
    overrideHeader: () => <KSpan text={lang.spotify_chart_entries.position} color="#111" fontWeight={500} fontSize={16} />,
    type: TABLE_COLUMN_TYPE.FUNCTION,
    render: (row: SpotifyChartEntry) => {
      const currentPosition = row?.position
      const prevPosition = row?.prevPosition
      const peakPosition = row?.peakPosition
      const entryStatus = row?.entryStatus
      let prevWeekChangeText = ""
      let color = ""
      let bgColor = ""
      let changeExist = false
      if (
        [EntryStatusOptions.NEW_ENTRY, EntryStatusOptions.RE_ENTRY, EntryStatusOptions.NO_CHANGE].includes(entryStatus)
      ) {
        prevWeekChangeText = EntryStatusLabelOptions[entryStatus as keyof typeof EntryStatusLabelOptions]
        if (entryStatus === EntryStatusOptions.NO_CHANGE) {
          color = "#818181"
          bgColor = "#8686861A"
        } else {
          color = "#0074E0"
          bgColor = "#2E77D01A"
        }
      } else {
        const prevWeekChange = prevPosition - currentPosition
        prevWeekChangeText = prevWeekChange.toString()
        color = prevWeekChange > 0 ? "#159542" : "#E91429"
        bgColor = prevWeekChange > 0 ? "#1DB9541A" : "#D01B2D1A"
        changeExist = true
      }

      return (
        <div
          className="h-[180px] py-3 pr-8"
          style={{
            borderRadius: 10
          }}
        >
          <div className="h-full flex flex-col justify-center gap-1">
            <div className="flex flex-col gap-3 items-start justify-center">
              <KSpan text={lang.spotify_chart_entries.current} fontSize={12} fontWeight={500} />
              <div className="flex flex-row items-center gap-3">
                <KSpan text={currentPosition?.toString()} color="#111" fontSize={34} fontWeight={500} />

                <div
                  className="flex flex-row items-center rounded-[20px] py-0.5 px-2 ml-[2px] mr-[-2px]"
                  style={{
                    backgroundColor: bgColor
                  }}
                >
                  {changeExist && (
                    <img
                      src={Number(prevWeekChangeText) > 0 ? ArrowUpIcon : ArrowDownIcon}
                      alt="change-icon"
                      className="ml-[-2px]"
                      height={18}
                      width={18}
                    />
                  )}
                  <KSpan
                    text={`${changeExist ? Math.abs(Number(prevWeekChangeText)) : prevWeekChangeText}`}
                    color={color}
                    fontWeight={600}
                    fontSize={14}
                    lineHeight="18px"
                  />
                </div>
                {changeExist && <KSpan text={lang.spotify_chart_entries.vs_last_week} color="#111" fontSize={13} lineHeight="18px" />}
              </div>
            </div>
            <div
              className="pt-4 mt-4 flex flex-row justify-start gap-20 w-min"
              style={{
                borderTop: "1px solid #F2F2F3"
              }}
            >
              <div className="flex flex-row items-center gap-1">
                <KSpan text={`${lang.spotify_chart_entries.previous}:`} />
                <KSpan text={prevPosition?.toString() ?? "-"} color="#111" fontWeight={600} />
              </div>
              <div className="flex flex-row items-center gap-1 whitespace-nowrap">
                <KTooltip
                  padding="8px"
                  position="bottom"
                  marginLeft="-20px"
                  content={
                    <div className="h-auto w-[230px] flex items-start justify-center flex-col gap-3 !whitespace-normal">
                      <KSpan
                        text={lang.spotify_chart_entries.peak_tooltip}
                        fontSize={12}
                        lineHeight="16px"
                        color="#111"
                      />
                    </div>
                  }
                >
                  <img src={InfoCircle} className="w-4 h-4 min-w-4 min-h-4" alt="trending-icon" />
                </KTooltip>
                <KSpan text={`${lang.spotify_chart_entries.peak}:`} />
                <KSpan text={peakPosition?.toString()} color="#111" fontWeight={600} />
              </div>
            </div>
            <div className="mt-2 flex flex-row gap-10">
              <div className="flex flex-row items-center gap-1">
                <KSpan text={`${lang.spotify_chart_entries.total_weeks_on_chart}:`} />
                <KSpan text={(row?.appearancesOnChart || "-").toString()} color="#111" fontWeight={600} />
              </div>
            </div>
          </div>
        </div>
      )
    }
  }

  const streamsColumn: TableColumn = {
    header: lang.spotify_chart_entries.streams,
    headerStyle: {
      textAlign: "left",
      width: "15%"
    },
    overrideHeader: () => <KSpan text={lang.spotify_chart_entries.streams} color="#111" fontWeight={500} fontSize={16} />,
    type: TABLE_COLUMN_TYPE.FUNCTION,
    render: (row: SpotifyChartEntry) => {
      const streams = row?.streams

      return (
        <div
          className="h-[180px] flex items-center"
          style={{
            borderRadius: 10
          }}
        >
          <div className="rounded-[4px] bg-[#F7F7F7] flex items-center p-1 gap-1 whitespace-nowrap w-min">
            <KSpan text={addCommasToNumber(streams || "-")?.toString()} color="#111" fontWeight={500} />
          </div>
        </div>
      )
    }
  }

  const columns: TableColumn[] = [spotifyChartColumn, entryColumn, positionColumn]
  if (selectedChartType === "track") columns.push(streamsColumn)
  return columns
}

export default spotifyChartEntriesColumns